<template>
  <div
    v-if="isNotAnEmployee"
    class="desktop-nav-cats-wrapper"
    v-click-outside="closeDept"
    @keydown.esc="closeDept"
  >
    <nav class="desktop-nav-cats" :aria-label="$t('subNavigation')">
      <div class="container-xl desktop-nav-cats__inner">
        <div v-if="hasDepartments" class="desktop-dept-wrap">
          <button
            type="button"
            class="desktop-cat-link primary-btn"
            :class="{ 'is-open': isDeptOpen }"
            aria-haspopup="true"
            :aria-expanded="isDeptOpen ? 'true' : 'false'"
            aria-controls="dept-dropdown"
            @click="toggleDept"
          >
            <font-awesome-icon icon="table-cells-large" />
            {{ $t('departments') }}
            <WCSvgIcon
              name="chevron-down"
              class="dept-chevron"
              :class="{ 'dept-chevron--open': isDeptOpen }"
              :icon-size="{ widthRem: 0.625, heightRem: 0.4375 }"
            />
          </button>

          <WCMultiColumnMenu
            :menuData="activeMenu"
            :isOpen="isDeptOpen"
            @selectSubcategory="onDeptSelect"
            @viewAll="onDeptViewAll"
            @visitHome="onDeptVisitHome"
            @close="closeDept"
          />
        </div>

        <div v-if="saveLinks.length" class="desktop-save-wrap" @click="closeDept">
          <WCCustomSelectModern
            :availableOptions="getSaveOptions($t)"
            :name="$t('save')"
            :unusableHeight="wcHeaderHeight"
            :dropdownLabel="$t('save')"
            btn-class="desktop-cat-link"
            btn-icon="tag"
            show-chevron
            @selectedOption="onSaveSelected"
          />
        </div>

        <router-link
          v-if="config?.listEnabled"
          class="desktop-cat-link"
          :to="{ name: QUICK_ACCESS_ROUTE.SHOPPING_LISTS }"
        >
          <font-awesome-icon icon="clipboard-list" />
          {{ $t('shoppingLists') }}
        </router-link>

        <router-link
          v-if="config?.pastItemsEnabled"
          class="desktop-cat-link"
          :to="{ name: QUICK_ACCESS_ROUTE.REORDER }"
        >
          <font-awesome-icon icon="clock-rotate-left" />
          {{ $t('reorder') }}
        </router-link>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute, type RouteLocationRaw } from 'vue-router';
import clickOutsidePlugin from 'click-outside-vue3';
import { configurationKey } from '@/injections';
import { GIFT_CARD_ROUTE_NAME } from '@/constants/AppConstants';
import useEmployee from '@/modules/employee/composables/useEmployee';
import WCCustomSelectModern from '@/modules/forms/components/WCCustomSelect/WCCustomSelectModern.vue';
import WCMultiColumnMenu from '@/components/WCMultiColumnMenu/WCMultiColumnMenu.vue';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';
import useOrderTypes from '@/composables/useOrderTypes';
import type { MenuNode } from '@/models/entities/menu-node';

interface SaveLink {
  id: string;
  labelKey: string;
  routeName: string;
  icon: string;
}

const vClickOutside = clickOutsidePlugin.directive;

const QUICK_ACCESS_ROUTE = {
  WEEKLY_AD: 'Circular',
  DIGITAL_COUPONS: 'Coupon Browse',
  REWARDS: 'Rewards',
  SHOPPING_LISTS: 'Lists',
  REORDER: 'Buy It Again',
};

withDefaults(defineProps<{ wcHeaderHeight?: string }>(), {
  wcHeaderHeight: '0px',
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { isNotAnEmployee } = useEmployee();
const { activeOrderType, navigateToOrderTypeHome } = useOrderTypes();
const config = inject(configurationKey);

const isDeptOpen = ref(false);
const activeMenu = ref<MenuNode | null>(null);

const hasDepartments = computed(
  () =>
    route.name !== GIFT_CARD_ROUTE_NAME &&
    !activeOrderType.value?.giftCard &&
    Boolean(activeMenu.value?.children?.length),
);

const saveLinks = computed<SaveLink[]>(() => {
  const links: SaveLink[] = [];
  if (config?.includeCircularLinkInMenu) {
    links.push({
      id: 'weeklyAd',
      labelKey: 'weeklyAd',
      routeName: QUICK_ACCESS_ROUTE.WEEKLY_AD,
      icon: 'newspaper',
    });
  }
  if (config?.allowCouponClipping) {
    links.push({
      id: 'digitalCoupons',
      labelKey: 'digitalCoupons',
      routeName: QUICK_ACCESS_ROUTE.DIGITAL_COUPONS,
      icon: 'ticket',
    });
  }
  if (config?.rewardsEnabled) {
    links.push({
      id: 'rewards',
      labelKey: 'rewardsAndLoyalty',
      routeName: QUICK_ACCESS_ROUTE.REWARDS,
      icon: 'star',
    });
  }
  return links;
});

function resolveActiveMenu(cartOrderTypeId: string | null): void {
  if (cartOrderTypeId) {
    let id = cartOrderTypeId;
    if (id.startsWith('WOT-')) id = id.substring(4);
    const orderType = config?.orderTypes?.find((ot: any) => ot.id === id);
    if (orderType?.menu) {
      activeMenu.value = {
        ...orderType.menu,
        name: orderType.name,
        orderTypeId: orderType.id,
      };
      return;
    }
  }
  if (config?.orderTypesEnabled && config?.orderTypes?.length) {
    const fallback = config.orderTypes.find((ot: any) => !ot.hidden && !ot.giftCard);
    if (fallback?.menu) {
      activeMenu.value = {
        ...fallback.menu,
        name: fallback.name,
        orderTypeId: fallback.id,
      };
      return;
    }
  }
  activeMenu.value = config?.mainMenu ?? null;
}

function getSaveOptions(translate: any) {
  return saveLinks.value.map(link => ({
    id: link.id,
    name: translate(link.labelKey),
    routeName: link.routeName,
    icon: link.icon,
  }));
}

function toggleDept() {
  isDeptOpen.value = !isDeptOpen.value;
}

function closeDept() {
  isDeptOpen.value = false;
}

function navigateTo(destination: RouteLocationRaw): void {
  router.push(destination).catch(() => {});
}

function onSaveSelected(option: { routeName?: string } | null): void {
  if (option && option.routeName) {
    navigateTo({ name: option.routeName });
  }
}

function onDeptSelect({ child }: { child: MenuNode }): void {
  navigateTo({ name: 'Browse', query: { g: child.id } });
  isDeptOpen.value = false;
}

function onDeptViewAll(dept: MenuNode): void {
  navigateTo({ name: 'Browse', query: { g: dept.id } });
  isDeptOpen.value = false;
}

function onDeptVisitHome(menu: MenuNode): void {
  if (menu?.orderTypeId) {
    navigateToOrderTypeHome(menu.orderTypeId);
  } else {
    navigateTo({ name: 'Browse' });
  }
  isDeptOpen.value = false;
}

watch(
  () => store.getters['cart/getCartOrderType'],
  (cartOrderTypeId: string | null) => resolveActiveMenu(cartOrderTypeId),
  { immediate: true },
);

watch(
  () => route.fullPath,
  () => {
    isDeptOpen.value = false;
  },
);
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.desktop-nav-cats-wrapper {
  position: relative;
}

.desktop-dept-wrap {
  position: relative;
}

.desktop-nav-cats {
  background-color: var(--primary, $primary);
}

.desktop-nav-cats__inner {
  display: flex;
  padding: 0 convert-px-to-rem(26);
}

.desktop-nav-cats :deep(.desktop-cat-link) {
  display: inline-flex;
  align-items: center;
  gap: convert-px-to-rem(6);
  padding: convert-px-to-rem(10) convert-px-to-rem(14);
  min-height: 2.5rem;
  font-size: $font-size-sm;
  font-weight: 400;
  line-height: 1;
  color: var(--white, $white);
  background: none;
  border: none;
  border-bottom: convert-px-to-rem(3) solid transparent;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.15s, border-color 0.15s;

  &:hover {
    color: var(--white, $white);
    background-color: rgba(255, 255, 255, 0.12);
    border-bottom-color: rgba(255, 255, 255, 0.7);
  }
}

.desktop-nav-cats :deep(.desktop-cat-link.primary-btn) {
  font-weight: 700;
}

.dept-chevron {
  color: var(--white, $white);
  transition: transform 0.3s ease;
}

.dept-chevron--open {
  transform: rotate(180deg);
}

.desktop-save-wrap :deep(.dropdown-menu) {
  margin-top: 0;
  min-width: convert-px-to-rem(220);
  padding: convert-px-to-rem(8) 0;
  border: convert-px-to-rem(1) solid #e5e5e5;
  border-radius: 0 convert-px-to-rem(8) convert-px-to-rem(8) convert-px-to-rem(8);
  box-shadow: 0 convert-px-to-rem(8) convert-px-to-rem(24) rgba($black, 0.15);
}

.desktop-save-wrap :deep(.dropdown-menu .dropdown-item) {
  padding: convert-px-to-rem(10) convert-px-to-rem(16);
  font-size: $font-size-sm;
  color: var(--dark-darker-2);
}

.desktop-save-wrap :deep(.dropdown-menu .dropdown-item:hover),
.desktop-save-wrap :deep(.dropdown-menu .dropdown-item:focus),
.desktop-save-wrap :deep(.dropdown-menu .dropdown-item.selected),
.desktop-save-wrap :deep(.dropdown-menu .dropdown-item.active) {
  background-color: var(--primary-lighter-9);
  color: var(--primary, $primary);
}
</style>
